import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Plus, Eye, EyeOff, Trash2 } from "lucide-react";
import { api, errMsg, fdate } from "../../lib/api";
import { PageTitle } from "./StaffLayout";

const AddForm = ({ lines, onDone }) => {
  const [f, setF] = useState({ line: lines[0]?.key || "", title: "", summary: "", ends_on: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/staff/cruise-deals", { ...f, ends_on: f.ends_on || null }); toast.success("Offer added"); setF({ ...f, title: "", summary: "", ends_on: "" }); onDone(); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <form onSubmit={save} className="card grid gap-3 p-4 sm:grid-cols-4" data-testid="add-deal-form">
      <select className="input" value={f.line} onChange={(e) => setF({ ...f, line: e.target.value })} data-testid="add-deal-line">{lines.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}</select>
      <input className="input sm:col-span-3" required placeholder="Offer headline" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="add-deal-title" />
      <input className="input sm:col-span-3" placeholder="Short description" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="add-deal-summary" />
      <input className="input" type="date" value={f.ends_on} onChange={(e) => setF({ ...f, ends_on: e.target.value })} data-testid="add-deal-ends" />
      <button className="btn-primary btn-sm sm:w-fit" data-testid="add-deal-submit"><Plus className="h-3 w-3" />Add offer</button>
    </form>
  );
};

const Row = ({ d, reload }) => {
  const patch = async (body) => { try { await api.patch(`/staff/cruise-deals/${d.id}`, body); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => {
    if (!window.confirm(d.source === "auto" ? "Remove this offer? It won't be added back by the daily check." : "Delete this offer?")) return;
    try { await api.delete(`/staff/cruise-deals/${d.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr data-testid={`deal-row-${d.id}`}>
      <td><b>{d.title}</b><div className="text-xs text-slate-500">{d.summary}</div></td>
      <td className="text-xs">{d.source === "auto" ? "Automatic" : "Added by staff"}</td>
      <td><input type="date" className="input py-1 text-xs" defaultValue={d.ends_on || ""} onBlur={(e) => e.target.value !== (d.ends_on || "") && patch({ ends_on: e.target.value })} data-testid={`deal-ends-${d.id}`} /></td>
      <td><span className={`chip ${d.live ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{d.hidden ? "Hidden" : d.live ? "Showing" : "Ended"}</span></td>
      <td className="whitespace-nowrap">
        <button className="btn-ghost btn-sm" onClick={() => patch({ hidden: !d.hidden })} data-testid={`deal-toggle-${d.id}`}>{d.hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}{d.hidden ? "Show" : "Hide"}</button>
        <button className="btn-ghost btn-sm text-red-600" onClick={del} aria-label="Delete offer" data-testid={`deal-delete-${d.id}`}><Trash2 className="h-3 w-3" /></button>
      </td>
    </tr>
  );
};

export default function CruiseDeals() {
  const [s, setS] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = () => api.get("/staff/cruise-deals").then((r) => setS(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); else toast.error(errMsg(e)); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!s?.running) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [s]);
  const refresh = async () => { const { data } = await api.post("/staff/cruise-deals/refresh"); toast.message(data.started ? "Checking the cruise lines now. This takes about a minute." : "A check is already running."); load(); };
  if (denied) return <p className="card p-6 text-slate-600" data-testid="cruise-deals-no-access">Cruise Deals is available to admins, managers and marketing staff.</p>;
  if (!s) return null;
  return (
    <div className="space-y-6" data-testid="cruise-deals-page">
      <PageTitle title="Cruise Deals" sub={`Checked automatically every morning${s.last_run ? ` · last checked ${fdate(s.last_run)}` : ""}. Ended offers hide themselves.`}>
        <button className="btn-primary btn-sm" onClick={refresh} disabled={s.running} data-testid="refresh-deals-btn"><RefreshCw className={`h-3 w-3 ${s.running ? "animate-spin" : ""}`} />{s.running ? "Checking…" : "Check now"}</button>
      </PageTitle>
      <AddForm lines={s.lines} onDone={load} />
      {s.lines.map((ln) => {
        const res = s.result?.[ln.key];
        const rows = s.deals.filter((d) => d.line === ln.key);
        return (
          <div key={ln.key} className="card overflow-x-auto p-4" data-testid={`staff-line-${ln.key}`}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><h3 className="text-lg font-semibold">{ln.name}</h3>
              {res && !res.ok && <span className="chip bg-amber-100 text-amber-800" data-testid={`line-error-${ln.key}`}>Last check failed; showing the previous offers</span>}
              <a href={ln.url} target="_blank" rel="noreferrer" className="text-xs underline">Open their deals page</a></div>
            {rows.length ? <table className="tbl"><thead><tr><th>Offer</th><th>Source</th><th>Ends</th><th>Status</th><th></th></tr></thead><tbody>{rows.map((d) => <Row key={d.id} d={d} reload={load} />)}</tbody></table>
              : <p className="text-sm text-slate-500">No offers yet.</p>}
          </div>
        );
      })}
    </div>
  );
}
